import React from 'react';

import { MarketingContentImgPreview, StatusChip } from '@occc-app/design-system';
import {
  MembershipOfferFragment,
  Offer_Engagement_Type,
  Offer_Visible_Status,
} from '@occc/graphql';

import { MakeTableFields } from '@occc/ui-design-system';
import { SourceFilterSelectOptionValueType } from './types';

const VISIBILITY_STATUS_TO_TEXT: Record<Offer_Visible_Status, string> = {
  [Offer_Visible_Status.AgeRestricted]: 'Age Restricted',
  [Offer_Visible_Status.Discontinued]: 'Discontinued',
  [Offer_Visible_Status.Expired]: 'Expired',
  [Offer_Visible_Status.GlobalLimit]: 'Global Limit Reached',
  [Offer_Visible_Status.MemberLimit]: 'Member Limit Reached',
  [Offer_Visible_Status.MemberNotActive]: 'Member Not Active',
  [Offer_Visible_Status.MemberSuspended]: 'Member Suspended',
  [Offer_Visible_Status.MissingRequiredSegments]: 'Missing Segments',
  [Offer_Visible_Status.NotInRequiredAudiences]: 'Not In Audiences',
  [Offer_Visible_Status.PurchaseLimitExceeded]: 'Purchase Limit Exceeded',
  [Offer_Visible_Status.PurchaseLimitNotReached]: 'Purchase Limit Not Reached',
  [Offer_Visible_Status.Unpublished]: 'Unpublished',
  [Offer_Visible_Status.UnsatisfiedMembershipProfileAttribute]: 'Unsatisfied',
  [Offer_Visible_Status.Visible]: 'Visible',
  [Offer_Visible_Status.NoVoucher]: 'No Voucher',
  [Offer_Visible_Status.HiddenUntilProgress]: 'Hidden Until Progress',
};

export const makeFields: MakeTableFields<MembershipOfferFragment> = (offer) => [
  {
    label: 'Image',
    value: (
      <MarketingContentImgPreview imageUrl={offer.marketingContents?.[0]?.imageUrl ?? undefined} />
    ),
  },
  {
    label: 'Name',
    value: offer.name,
  },
  {
    label: 'Description',
    value: offer.description || '',
  },
  {
    label: 'Fulfillment Count',
    value:
      offer.engagementType === Offer_Engagement_Type.Billboard ? 'N/A' : offer.fulfillmentCount,
  },
  {
    label: 'Eligibility',
    value: (
      <StatusChip
        variant={offer.eligible ? 'success' : 'error'}
        label={offer.eligible ? 'Eligible' : 'Not Eligible'}
      />
    ),
  },
  {
    label: 'Status',
    value: offer.visibleStatus ? (
      <StatusChip
        variant={offer.visibleStatus === Offer_Visible_Status.Visible ? 'success' : 'error'}
        label={VISIBILITY_STATUS_TO_TEXT[offer.visibleStatus]}
      />
    ) : (
      ''
    ),
  },
];

export const headers = [
  { label: '' },
  { label: 'Name' },
  { label: 'Description' },
  { label: 'Fulfillment Count' },
  { label: 'Eligibility' },
  { label: 'Status' },
];

export const sourceFilterOptionsMap: Record<
  SourceFilterSelectOptionValueType,
  Offer_Engagement_Type[]
> = {
  [SourceFilterSelectOptionValueType.Reward]: [Offer_Engagement_Type.Purchaseable],
  [SourceFilterSelectOptionValueType.Promotion]: [
    Offer_Engagement_Type.Promotional,
    Offer_Engagement_Type.Promocode,
  ],
  [SourceFilterSelectOptionValueType.OfferChallenge]: [Offer_Engagement_Type.Transactional],
  [SourceFilterSelectOptionValueType.Punchcard]: [Offer_Engagement_Type.PunchcardChallenge],
  [SourceFilterSelectOptionValueType.Awards]: [
    Offer_Engagement_Type.Vouchered,
    Offer_Engagement_Type.SweepstakesEntry,
  ],
  [SourceFilterSelectOptionValueType.Sweepstakes]: [
    Offer_Engagement_Type.SweepstakesPurchasable,
    Offer_Engagement_Type.SweepstakesTransactional,
  ],
  [SourceFilterSelectOptionValueType.Games]: [Offer_Engagement_Type.Game],
  [SourceFilterSelectOptionValueType.Billboards]: [Offer_Engagement_Type.Billboard],
};
